import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { 
  Search, Users, FileText, Euro, ArrowUpDown, ExternalLink
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export default function Fournisseurs() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("total"); // total, count, name
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchFournisseurs = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use the aggregation endpoint - more efficient and no limit
      const res = await axios.get(`${API}/supplier-invoices/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const suppliers = res.data.suppliers || [];
      
      // Transform to expected format
      const fournisseurList = suppliers.map(s => ({
        name: s.name,
        total_ttc: s.total_ttc || 0,
        total_ht: s.total_ht || 0,
        count: s.count || 0,
        lastInvoice: s.last_invoice_date || null
      }));
      
      setFournisseurs(fournisseurList);
    } catch (error) {
      console.error("Error fetching fournisseurs:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFournisseurs();
  }, [fetchFournisseurs]);

  // Filter and sort
  const filteredFournisseurs = fournisseurs
    .filter(f => f.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'count':
          comparison = a.count - b.count;
          break;
        case 'total':
        default:
          comparison = a.total_ttc - b.total_ttc;
          break;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const totalAchats = fournisseurs.reduce((sum, f) => sum + f.total_ttc, 0);
  const totalFactures = fournisseurs.reduce((sum, f) => sum + f.count, 0);

  const viewFournisseurInvoices = (fournisseurName) => {
    // Navigate to factures-fournisseurs with search filter
    navigate(`/factures-fournisseurs?search=${encodeURIComponent(fournisseurName)}`);
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="fournisseurs-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
          <Users className="w-8 h-8 text-primary" />
          Fournisseurs
        </h1>
        <p className="text-muted-foreground mt-1">
          {fournisseurs.length} fournisseur{fournisseurs.length !== 1 ? 's' : ''} enregistré{fournisseurs.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Fournisseurs</p>
                <p className="text-xl md:text-2xl font-bold tabular-nums mt-1">
                  {fournisseurs.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Factures</p>
                <p className="text-xl md:text-2xl font-bold tabular-nums mt-1">
                  {totalFactures}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Euro className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total Achats</p>
                <p className="text-xl md:text-2xl font-bold text-primary tabular-nums mt-1">
                  {formatCurrency(totalAchats)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un fournisseur..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 border-b border-slate-200">
                  <TableHead 
                    className="text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Fournisseur
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('count')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Factures
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right cursor-pointer hover:bg-slate-100"
                    onClick={() => toggleSort('total')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Total TTC
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">
                    Dernière facture
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filteredFournisseurs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Aucun fournisseur trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFournisseurs.map((fournisseur, idx) => (
                    <TableRow 
                      key={fournisseur.name}
                      className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
                      onClick={() => viewFournisseurInvoices(fournisseur.name)}
                      data-testid={`fournisseur-row-${idx}`}
                    >
                      <TableCell className="font-medium">
                        {fournisseur.name}
                      </TableCell>
                      <TableCell className="text-center tabular-nums">
                        {fournisseur.count}
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium tabular-nums text-primary">
                        {formatCurrency(fournisseur.total_ttc)}
                      </TableCell>
                      <TableCell className="text-center text-sm text-muted-foreground tabular-nums">
                        {fournisseur.lastInvoice 
                          ? new Date(fournisseur.lastInvoice).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
